// src/layouts/Footer.jsx
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail, ArrowUp } from "lucide-react";

const socialLinks = [
  { name: "Facebook", icon: Facebook, href: "#" },
  { name: "Instagram", icon: Instagram, href: "#" },
  { name: "LinkedIn", icon: Linkedin, href: "#" },
];

const quickLinks = [
  { name: "Home", href: "/" },
  { name: "About Us", href: "/about" },
  { name: "Our Team", href: "/team" },
  { name: "Subunits", href: "/subunits" },
];

const discoverLinks = [
  { name: "Events", href: "/events" },
  { name: "Awards", href: "/awards" },
  { name: "Contact", href: "/contact" },
  { name: "Privacy Policy", href: "/privacy" },
];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

function LinkColumn({ title, links, delay }) {
  return (
    <motion.div {...reveal(delay)}>
      <h5 className="text-sm font-bold text-[#7dd3fc] mb-5 pb-3 border-b border-white/15">{title}</h5>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.name}>
            <Link
              to={link.href}
              className="group inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc]"
            >
              <span className="h-[2px] w-0 bg-[#7dd3fc] transition-all duration-300 group-hover:w-4" />
              {link.name}
            </Link>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-[#00334d] overflow-hidden border-t-[3px] border-[#7dd3fc]">
      {/* blueprint grid */}
      <div
        className="absolute inset-0 opacity-[0.1] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* corner brackets */}
      <span className="absolute top-6 left-4 sm:left-8 w-8 h-8 border-t-2 border-l-2 border-[#7cc4e8]/70 pointer-events-none" />
      <span className="absolute top-6 right-4 sm:right-8 w-8 h-8 border-t-2 border-r-2 border-[#7cc4e8]/70 pointer-events-none" />

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-12 pt-16 md:pt-20">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-12 pb-14">
          {/* Brand */}
          <motion.div {...reveal(0)} className="col-span-2 lg:col-span-4">
            <img
              src="/logosb.png"
              alt="IEEE ISIMS SB Logo"
              className="w-36 h-auto mb-6 brightness-0 invert"
            />
            <p className="text-white/70 text-base leading-relaxed max-w-[38ch]">
              Empowering students to build skills, innovate, and change the world through technology and community.
            </p>
            <div className="flex gap-2 mt-7">
              {socialLinks.map(({ name, icon: Icon, href }) => (
                <a
                  key={name}
                  href={href}
                  aria-label={name}
                  title={name}
                  className="w-11 h-11 flex items-center justify-center bg-white/10 text-white border border-white/25 hover:bg-white hover:text-[#00334d] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc]"
                >
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </a>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-2 lg:col-start-6">
            <LinkColumn title="Quick links" links={quickLinks} delay={0.08} />
          </div>
          <div className="lg:col-span-2">
            <LinkColumn title="Discover" links={discoverLinks} delay={0.14} />
          </div>

          {/* Contact */}
          <motion.div {...reveal(0.2)} className="col-span-2 lg:col-span-3">
            <h5 className="text-sm font-bold text-[#7dd3fc] mb-5 pb-3 border-b border-white/15">Get in touch</h5>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-white/75">
                <MapPin className="w-5 h-5 text-[#7dd3fc] mt-0.5 flex-shrink-0" strokeWidth={2} />
                <p className="text-sm font-medium leading-relaxed">
                  Sfax, rue Tunis klm 11<br />Sakiet Ezzit, Sfax 3021
                </p>
              </li>
              <li>
                <a href="tel:+21624494492" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-[#7dd3fc] flex-shrink-0" strokeWidth={2} />
                  <span className="text-sm font-medium">+216 24 494 492</span>
                </a>
              </li>
              <li>
                <a href="mailto:sb.isims@ieee.org" className="flex items-center gap-3 text-white/75 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-[#7dd3fc] flex-shrink-0" strokeWidth={2} />
                  <span className="text-sm font-medium">sb.isims@ieee.org</span>
                </a>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* giant outlined wordmark, cropped by the bottom bar */}
      <div className="relative z-10 pointer-events-none select-none -mb-[0.14em] text-[19vw] lg:text-[15rem] leading-[0.8]" aria-hidden="true">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-12">
          <span
            className="block font-black tracking-tighter text-transparent whitespace-nowrap"
            style={{ WebkitTextStroke: "1.5px rgba(124,196,232,0.35)" }}
          >
            IEEE ISIMS
          </span>
        </div>
      </div>

      {/* bottom bar */}
      <div className="relative z-10 bg-[#00263a] border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-12 py-5 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <p className="text-white/60 text-xs font-semibold">
            © {new Date().getFullYear()} IEEE ISIMS Student Branch. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a
              href="https://mt-portfolio-ashen.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 text-xs font-semibold hover:text-white transition-colors"
            >
              Developed by{" "}
              <span className="font-bold text-white underline decoration-[#7dd3fc] underline-offset-4">Mohamed Tounsi</span>
            </a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
              className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/25 hover:bg-white hover:text-[#00334d] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc]"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}